import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { RatingDistribution } from '@/types/feedback';

// 1★ red → 5★ teal, so the histogram reads as a sentiment gradient.
const BAR_COLORS = [
  'var(--destructive, #ef4444)',
  'var(--warning-500, #f59e0b)',
  'var(--muted-foreground, #a1a1aa)',
  'var(--chart-2, #2dd4bf)',
  'var(--chart-1, #14b8a6)',
];

/**
 * Straight histogram of the 1-5 star distribution over the current (filtered)
 * scope — when a single feature is selected, this is that feature's distribution.
 */
export function RatingHistogram({
  distribution,
  scopeLabel,
}: {
  distribution: RatingDistribution;
  scopeLabel?: string;
}) {
  const data = [
    { rating: '1★', count: distribution.star1 },
    { rating: '2★', count: distribution.star2 },
    { rating: '3★', count: distribution.star3 },
    { rating: '4★', count: distribution.star4 },
    { rating: '5★', count: distribution.star5 },
  ];
  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Rating distribution</CardTitle>
        {scopeLabel && <p className="text-xs text-muted-foreground">{scopeLabel}</p>}
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No ratings in this period</p>
        ) : (
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="rating" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} className="text-muted-foreground" />
                <Tooltip cursor={{ fill: 'var(--muted, #f4f4f5)', opacity: 0.4 }} />
                <Bar dataKey="count" name="Responses" radius={[4, 4, 0, 0]}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
